import React, { useEffect, useRef } from 'react';
import styles from './secondary-navigation.module.scss';
import classNames from 'classnames';
import type { NavigationItem } from '@app/shared/components/secondary-navigation/secondary-navigation-types';
import { SecondaryNavigationVariation } from '@app/shared/components/secondary-navigation/secondary-navigation-enums';
import { SecondaryNavigationItem } from './SecondaryNavigationItem';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ContainerTestId } from '@app/shared/models/contracts/enums/shared-enums';

type SecondaryNavigationProps = {
  navigationItems: NavigationItem[];
  variation?: SecondaryNavigationVariation;
  updateCaseOrder: (navigationItems: NavigationItem[]) => void;
};

type DragItem = {
  index: number;
  id: string;
  type: string;
};

const moveItem = (items: NavigationItem[], from: number, to: number) => {
  const updated = [...items];
  const [removed] = updated.splice(from, 1);
  updated.splice(to, 0, removed);
  updated.forEach((item, idx) => {
    item.order = idx;
  });
  return updated;
};

interface DraggableNavItemProps {
  item: NavigationItem;
  index: number;
  moveNavItem: (from: number, to: number) => void;
}

const DraggableNavItem: React.FC<DraggableNavItemProps> = ({ item, index, moveNavItem }) => {
  const ref = useRef<HTMLLIElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: 'NAV_ITEM',
    item: { index, id: item.params!.caseId, type: 'NAV_ITEM' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const [, drop] = useDrop({
    accept: 'NAV_ITEM',
    hover: (dragged: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      // Only move when cursor crosses halfway horizontally
      if (dragged.index < index && hoverClientX > hoverMiddleX) {
        // Dragging right
        moveNavItem(dragged.index, index);
        dragged.index = index;
      } else if (dragged.index > index && hoverClientX < hoverMiddleX) {
        // Dragging left
        moveNavItem(dragged.index, index);
        dragged.index = index;
      }
    },
    drop: (dragged: DragItem) => {
      if (dragged.index !== index) {
        moveNavItem(dragged.index, index);
      }
    },
  });
  drag(drop(ref));
  return (
    <li
      ref={ref}
      className={classNames(styles['secondary-navigation__item-container'], {
        dragging: isDragging,
      })}
      data-testid={`draggable-item-${item.label.toLowerCase().replaceAll(' ', '-')}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}>
      <SecondaryNavigationItem
        route={item.route}
        params={item.params}
        additionalData={item.additionalData}>
        {item.label}
      </SecondaryNavigationItem>
    </li>
  );
};

export const DragAndDropSecondaryNavigation = ({
  navigationItems,
  variation,
  updateCaseOrder,
}: SecondaryNavigationProps) => {
  const mainElement = useRef<Element | null>(null);
  const [items, setItems] = React.useState(navigationItems);

  useEffect(() => {
    setItems(navigationItems);
  }, [navigationItems]);

  useEffect(() => {
    mainElement.current = document.querySelector(`[data-testid=${ContainerTestId.Main}]`);
  }, []);

  const moveNavItem = (from: number, to: number) => {
    const updated = moveItem(items, from, to);
    setItems(updated);
    updateCaseOrder(updated);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <ul
          data-testid="dnd-secondary-navigation"
          className={classNames(styles['secondary-navigation'], {
            [styles[`secondary-navigation--${variation}`]]: variation,
          })}>
          {items.map((item, index) => (
            <DraggableNavItem
              key={item.params!.caseId}
              item={item}
              index={index}
              moveNavItem={moveNavItem}
            />
          ))}
        </ul>
      </div>
    </DndProvider>
  );
};
